import { ref } from 'vue'
import { claudeUsageApi } from '@/api/claudeUsage'
import { withAsyncState } from '@/lib/asyncState'
import type { ClaudeUsageSnapshot } from '@/types/models'

const MIN_FETCH_INTERVAL_MS = 5 * 60 * 1000

// Singleton, following useQueueMonitor.ts's shape. AppShell (the only
// caller) is rendered per-view rather than as a persistent router layout, so
// it fully remounts on every navigation — module-level state, and the
// lastFetchedAt gate below, are what keep the 5-minute cadence the ticket
// requires instead of every click around the app issuing a fresh request.
const snapshot = ref<ClaudeUsageSnapshot | null>(null)
const error = ref<string | null>(null)
let lastFetchedAt = 0

export function useClaudeUsage() {
  async function fetchSnapshot(): Promise<void> {
    if (Date.now() - lastFetchedAt < MIN_FETCH_INTERVAL_MS) return

    lastFetchedAt = Date.now()
    await withAsyncState(error, 'Failed to load Claude usage.', async () => {
      snapshot.value = await claudeUsageApi.current()
    })
  }

  // Called on logout so the next session doesn't briefly show the previous
  // account's usage before its own fetch resolves, same as
  // useQueueMonitor.reset().
  function reset(): void {
    snapshot.value = null
    error.value = null
    lastFetchedAt = 0
  }

  return {
    snapshot,
    error,
    fetchSnapshot,
    reset,
  }
}
